/**
 * Okanagan Wine Guide - API Service
 * Connects to the Wine Route Planner API at n8nn8n.ca
 */

const API_BASE = 'https://app.n8nn8n.ca';

// ============================================
// Visited Wineries Memory (localStorage)
// Tracks winery IDs visited in the past 14 days
// so the same wineries aren't repeated on new routes
// ============================================

const VISITED_KEY = 'okanagan_visited_wineries';
const VISITED_EXPIRY_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function readVisitedStore() {
  try {
    const raw = localStorage.getItem(VISITED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeVisitedStore(entries) {
  try {
    localStorage.setItem(VISITED_KEY, JSON.stringify(entries));
  } catch {
    // localStorage unavailable (private mode, storage full, etc.)
  }
}

/**
 * Save winery IDs from a completed route.
 * Called after getWineRoute() returns successfully.
 * @param {Array} stops - The stops array from the route response
 */
export function saveVisitedWineries(stops) {
  const now = Date.now();
  const existing = readVisitedStore();
  const updated = [...existing];

  for (const stop of stops) {
    if (!stop.id) continue;
    const idx = updated.findIndex(e => e.id === stop.id);
    if (idx >= 0) {
      updated[idx].ts = now; // refresh timestamp if visited again
    } else {
      updated.push({ id: stop.id, ts: now });
    }
  }

  // Cap at 30 most-recent entries so the excluded list never blocks most of a region
  const capped = updated.sort((a, b) => b.ts - a.ts).slice(0, 30);
  writeVisitedStore(capped);
}

export function clearVisitedWineries() {
  writeVisitedStore([]);
}

/**
 * Return IDs of wineries visited within the past 14 days.
 * Also prunes expired entries from storage as a side effect.
 * @returns {string[]} Array of winery UUIDs to exclude
 */
function getExcludedWineryIds() {
  const cutoff = Date.now() - VISITED_EXPIRY_MS;
  const all = readVisitedStore();
  const active = all.filter(e => e.ts > cutoff);

  if (active.length !== all.length) {
    writeVisitedStore(active); // prune expired entries
  }

  return active.map(e => e.id).filter(Boolean);
}

/**
 * Submit a winery rating
 * @param {string} wineryId - The winery's database ID
 * @param {number} rating - Rating from 1-5
 * @param {string} comment - Optional comment
 * @returns {Promise<Object>} Submission result
 */
export async function submitWineryRating(wineryId, rating, comment = '') {
  const response = await fetch(`${API_BASE}/webhook/winery-rating`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      winery_id: wineryId,
      rating: rating,
      comment: comment,
      submitted_at: new Date().toISOString()
    }),
  });

  if (!response.ok) {
    const error = new Error('Failed to submit rating');
    error.status = response.status;
    throw error;
  }

  return response.json();
}

/**
 * Get average rating for a winery
 * @param {string} wineryId - The winery's database ID
 * @returns {Promise<Object>} Rating data with average and count
 */
export async function getWineryRating(wineryId) {
  try {
    const response = await fetch(`${API_BASE}/webhook/get-winery-rating?wineryId=${wineryId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return { average: null, count: 0 };
    }

    return response.json();
  } catch (error) {
    console.error('Failed to fetch rating:', error);
    return { average: null, count: 0 };
  }
}

/**
 * Check for wildfires, evacuation alerts, and air quality near the user's location and route
 * @param {number} lat - User latitude
 * @param {number} lng - User longitude
 * @param {Array} routeStops - Array of {lat, lng, name} stop objects
 * @returns {Promise<Object>} { severity, alerts }
 */
export async function checkSafety(lat, lng, routeStops = []) {
  try {
    const response = await fetch(`${API_BASE}/webhook/okanagan-safety-check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lat,
        lng,
        route_stops: routeStops.map(s => ({ lat: s.lat ?? s.latitude, lng: s.lng ?? s.longitude, name: s.name })),
      }),
    });
    if (!response.ok) return { severity: 'none', alerts: [] };
    return response.json();
  } catch {
    return { severity: 'none', alerts: [] };
  }
}

/**
 * Get a wine route based on duration and region
 * @param {string} routeType - "2-hour", "half-day", or "full-day"
 * @param {string} region - "oliver", "osoyoos", "penticton", "naramata", "summerland", "kelowna", "west-kelowna"
 * @param {Object} startLocation - Optional {lat, lng} for optimized routing
 * @param {Object} preferences - Optional wine preferences {wineType, amenities}
 * @returns {Promise<Object>} Route data with stops
 */
export async function getWineRoute(routeType, region, startLocation = null, preferences = null, source = 'direct', includeDining = false) {
  const requestBody = {
    route_type: routeType,
    region: region.toLowerCase(),
    source,
  };

  // Include start location if available for route optimization
  if (startLocation && startLocation.lat && startLocation.lng) {
    requestBody.start_location = {
      lat: startLocation.lat,
      lng: startLocation.lng
    };
  }

  // Exclude wineries visited in the past 14 days
  const excludedIds = getExcludedWineryIds();
  if (excludedIds.length > 0) {
    requestBody.excluded_winery_ids = excludedIds;
  }

  if (includeDining) {
    requestBody.include_dining = true;
  }

  // Include wine preferences if specified
  if (preferences) {
    if (preferences.wineType && preferences.wineType !== 'any') {
      requestBody.wine_type = preferences.wineType; // 'reds' or 'whites'
    }
    if (preferences.selectedWines && preferences.selectedWines.length > 0) {
      requestBody.selected_wines = preferences.selectedWines; // specific wine varieties
    }
    if (preferences.amenities && preferences.amenities.length > 0) {
      requestBody.amenities = preferences.amenities; // ['pet_friendly', 'picnic_area', 'restaurant']
    }
  }

  const response = await fetch(`${API_BASE}/webhook/wine-route`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const error = new Error('Failed to fetch wine route');
    error.status = response.status;
    throw error;
  }

  const data = await response.json();

  // Handle no-wineries case: clear visit history so the next tap works
  if (data.success === false && data.error === 'no_wineries_found') {
    if (requestBody.excluded_winery_ids && requestBody.excluded_winery_ids.length > 0) {
      writeVisitedStore([]);
      throw new Error('You\'ve visited all the wineries matching your preferences! Your history has been reset — tap Build again for a fresh route.');
    }
    throw new Error('No wineries found for this region and preferences. Try selecting a different area or fewer wine filters.');
  }

  // Handle the actual API response structure: { success, route: { type, region, totalStops, stops: [...] } }
  const route = data.route;

  // Check if we have valid route data
  if (!route || !route.stops) {
    throw new Error('Invalid route data received');
  }

  // Transform API response to match frontend component field names
  return {
    route_name: route.type || 'Wine Route',
    dining_included: data.dining_included ?? null,
    total_stops: route.totalStops || route.stops.length,
    estimated_time: route.estimatedTime?.total || route.estimatedTime || 'Varies',
    stops: route.stops.map((stop, index) => ({
      id: stop.id, // Winery UUID for ratings
      stop_number: stop.stop || index + 1,
      name: stop.name || 'Unknown Winery',
      address: stop.address || '',
      latitude: stop.coordinates?.lat || 0,
      longitude: stop.coordinates?.lng || 0,
      phone: stop.phone || '',
      website: stop.website || '',
      // Transform API field names to frontend field names
      image_url: stop.card?.imageUrl || '',
      revised_description: stop.card?.description || '',
      hours_text: stop.card?.hours || '',
      events: stop.card?.events || '',
      features: stop.card?.features || [],
      vineyard_experiences: stop.card?.experiences || [],
      wines_offered: stop.card?.wines || [],
      restaurant_open: stop.card?.restaurantOpen || false,
      has_restaurant: stop.card?.hasRestaurant || false,
      tasting_fee: stop.card?.tastingFee || null,
      tasting_fee_updated_at: stop.card?.tastingFeeUpdatedAt || null,
      reservation_required: stop.card?.reservationRequired ?? null,
      food_service: stop.card?.foodService || null,
      // Add region from request since it's not in API response
      region: formatRegionName(region)
    }))
  };
}

/**
 * Search for wineries by name or keyword
 * @param {string} query - Search term
 * @returns {Promise<Array>} Array of matching wineries
 */
export async function searchWineries(query) {
  const response = await fetch(`${API_BASE}/webhook/wineries/search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ region: 'all' }),
  });

  if (!response.ok) {
    throw new Error('Failed to search wineries');
  }

  const data = await response.json();
  const allWineries = data.wineries || [];

  // Client-side filtering for better reliability
  const searchLower = query.toLowerCase();
  const filtered = allWineries.filter(w =>
    w.name?.toLowerCase().includes(searchLower) ||
    w.address?.toLowerCase().includes(searchLower) ||
    w.region_key?.toLowerCase().includes(searchLower)
  );

  return transformWineryList(filtered);
}

/**
 * Get all wineries in a region
 * @param {string} region - Region name
 * @returns {Promise<Array>} Array of wineries
 */
export async function getWineriesByRegion(region) {
  // This endpoint can be added later to your n8n workflows
  const response = await fetch(`${API_BASE}/webhook/wineries/${region.toLowerCase()}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch wineries');
  }

  const data = await response.json();
  return transformWineryList(data.wineries || []);
}

/**
 * Get wine varieties available in a region
 * Scans wineries in the region and returns unique wine types grouped by red/white
 * @param {string} region - Region name
 * @returns {Promise<Object>} Wine varieties grouped by type { reds: [...], whites: [...] }
 */
export async function getWinesByRegion(region) {
  try {
    const response = await fetch(`${API_BASE}/webhook/wines-by-region`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ region: region.toLowerCase() }),
    });

    if (!response.ok) {
      // Return fallback wine list if endpoint not available
      return getDefaultWineList();
    }

    const data = await response.json();
    return {
      reds: data.reds || [],
      whites: data.whites || []
    };
  } catch (error) {
    console.error('Failed to fetch wines by region:', error);
    // Return fallback wine list
    return getDefaultWineList();
  }
}

/**
 * Default wine varieties for Okanagan region
 * Used as fallback when API is unavailable
 */
function getDefaultWineList() {
  return {
    reds: [
      { id: 'Pinot Noir', name: 'Pinot Noir' },
      { id: 'Merlot', name: 'Merlot' },
      { id: 'Cabernet Sauvignon', name: 'Cabernet Sauvignon' },
      { id: 'Cabernet Franc', name: 'Cabernet Franc' },
      { id: 'Syrah', name: 'Syrah' },
      { id: 'Malbec', name: 'Malbec' },
      { id: 'Gamay Noir', name: 'Gamay Noir' },
    ],
    whites: [
      { id: 'Chardonnay', name: 'Chardonnay' },
      { id: 'Pinot Gris', name: 'Pinot Gris' },
      { id: 'Riesling', name: 'Riesling' },
      { id: 'Gewürztraminer', name: 'Gewürztraminer' },
      { id: 'Sauvignon Blanc', name: 'Sauvignon Blanc' },
      { id: 'Viognier', name: 'Viognier' },
      { id: 'Pinot Blanc', name: 'Pinot Blanc' },
    ]
  };
}

/**
 * Get details for a single winery
 * @param {number} wineryId - Winery ID
 * @returns {Promise<Object>} Winery details
 */
export async function getWineryDetails(wineryId) {
  // This endpoint can be added later to your n8n workflows
  const response = await fetch(`${API_BASE}/webhook/winery/${wineryId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch winery details');
  }

  const data = await response.json();
  return transformWinery(data);
}

// ============================================
// Helper Functions
// ============================================

/**
 * Transform a single winery object from API to frontend format
 */
function transformWinery(winery) {
  return {
    id: winery.id,
    name: winery.name,
    address: winery.address,
    latitude: winery.latitude,
    longitude: winery.longitude,
    phone: winery.phone,
    website: winery.website,
    image_url: winery.imageUrl || winery.image_url,
    description: winery.description,
    revised_description: winery.revised_description,
    hours_text: winery.hours || winery.hours_text,
    events: winery.events,
    region: winery.region,
    // Additional fields that may come from enhanced data
    vineyard_experiences: winery.vineyard_experiences || [],
    grapes_grown: winery.grapes_grown || [],
    wines_offered: winery.wines_offered || [],
    features: winery.features || [],
    reservation_required: winery.reservation_required,
    food_service: winery.food_service || null,
    tasting_fee: winery.tasting_fee || null,
    tasting_fee_updated_at: winery.tasting_fee_updated_at || null,
    restaurant_open: winery.restaurant_open || false,
    has_restaurant: winery.has_restaurant || false,
    hours_structured: winery.hours_structured,
    last_verified: winery.last_verified,
  };
}

/**
 * Transform an array of wineries
 */
function transformWineryList(wineries) {
  return wineries.map(transformWinery);
}

/**
 * Format region name for display
 */
function formatRegionName(region) {
  const regionNames = {
    'oliver': 'Oliver / Golden Mile Bench',
    'osoyoos': 'Osoyoos / Black Sage',
    'ok-falls': 'OK Falls',
    'kaleden': 'Kaleden',
    'penticton': 'Penticton',
    'naramata': 'Naramata',
    'summerland': 'Summerland',
    'kelowna': 'Kelowna',
    'west-kelowna': 'West Kelowna',
    'lake-country': 'Lake Country',
    'keremeos': 'Keremeos',
    'skaha-bench': 'Skaha Bench',
    'peachland': 'Peachland',
  };
  return regionNames[region.toLowerCase()] || region;
}

// ============================================
// Event Tracking
// ============================================

const SESSION_ID_KEY = 'okanagan_session_id';

function getSessionId() {
  let id = sessionStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}

function getDeviceType() {
  return /Mobi|Android|iPhone|iPad/.test(navigator.userAgent) ? 'mobile' : 'desktop';
}

/**
 * Log a winery interaction event (fire-and-forget)
 * event_type: 'view_detail' | 'skip' | 'done' | 'share' | 'navigate'
 */
export function logWineryEvent(eventType, winery = null, region = null) {
  try {
    fetch(`${API_BASE}/webhook/winery-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: eventType,
        winery_id: winery?.id || null,
        winery_name: winery?.name || null,
        region: region || winery?.region || null,
        device_type: getDeviceType(),
        session_id: getSessionId(),
      }),
    }).catch(() => {}); // silently ignore failures
  } catch (e) {}
}

/**
 * Calculate distance between two coordinates (Haversine formula)
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} Distance in kilometers
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c * 10) / 10; // Round to 1 decimal
}

function toRad(deg) {
  return deg * (Math.PI / 180);
}

// ============================================
// Route Type Helpers
// ============================================

export const ROUTE_TYPES = {
  '2-hour': {
    label: '2 Hours',
    description: '2 wineries, quick tasting tour',
    stops: 2,
    icon: '🍷'
  },
  'half-day': {
    label: 'Half Day',
    description: '4 wineries, leisurely exploration',
    stops: 4,
    icon: '🍇'
  },
  'full-day': {
    label: 'Full Day',
    description: '7 wineries, complete wine adventure',
    stops: 7,
    icon: '🏆'
  }
};

export const REGIONS = [
  { value: 'oliver', label: 'Oliver', description: 'Wine Capital of Canada' },
  { value: 'osoyoos', label: 'Osoyoos', description: 'Desert wine country' },
  { value: 'ok-falls', label: 'OK Falls', description: 'Okanagan Falls wine trail' },
  { value: 'kaleden', label: 'Kaleden', description: 'Hidden gem wine country' },
  { value: 'penticton', label: 'Penticton', description: 'Between two lakes' },
  { value: 'naramata', label: 'Naramata', description: 'Naramata Bench' },
  { value: 'summerland', label: 'Summerland', description: 'Organic wine hub' },
  { value: 'kelowna', label: 'Kelowna', description: 'Urban wine experience' },
  { value: 'west-kelowna', label: 'West Kelowna', description: 'Westside wine trail' },
  { value: 'lake-country', label: 'Lake Country', description: 'North Okanagan wine trail' },
  { value: 'keremeos', label: 'Keremeos', description: 'Similkameen Valley wines' },
  { value: 'skaha-bench', label: 'Skaha Bench', description: 'Emerging wine frontier' },
  { value: 'peachland', label: 'Peachland', description: 'Lakeshore wine country' },
];
